import { CreatePointRequest, CreateTagRequest, CreateTopicRequest, CreateTopicResponse, DiscoveryLevel, ObjectId, TopicCreationSource } from '../types';
import { addTopic, getTopic } from './api/topics-api';

import accountInfo from './state/account-info';
import { addPoints } from './api/points-api';
import { getFromLocalStorage } from './utils';
import { logWithAmplitude } from './api/api';
import pointCache from './state/point-cache';
import topicData from './state/topic-data';
import { ErrorToastElement } from '../components/error-toast';

export async function onBuildMapClick(accountInformation: typeof accountInfo,
                                      topicInfo: typeof topicData,
                                      pointCacheData: typeof pointCache,
                                      errorToast: ErrorToastElement,
                                      amplitudeCallsite: string) {

    logWithAmplitude(amplitudeCallsite, 'Build Your Own Map Click', {'topic_id': topicInfo.topicId});

    const tag_info: CreateTagRequest = {
        user_tags: [],
    };

    const email = accountInformation.isAuthenticated ? accountInformation.email : (getFromLocalStorage('email') ?? undefined);

    const createTopicRequest: CreateTopicRequest = {
        name: 'My Proxi Map',
        email,
        organization_id: undefined,
        use_organization_template: false,
        creator: undefined,
        categories: topicInfo.categories,
        has_dates: topicInfo.topic?.has_dates ?? false,
        should_filter_past_events: topicInfo.topic?.should_filter_past_events ?? false,
        has_likes: false,
        discovery_level: DiscoveryLevel.Private,
        disable_google_images: false,
        hide_header: false,
        show_advanced_filter_row: false,
        custom_font: undefined,
        requires_approval: false,
        send_crowdsource_email: true,
        default_map_center: topicInfo.topic?.default_map_center,
        default_map_zoom: topicInfo.topic?.default_map_zoom,
        default_map_rotation: topicInfo.topic?.default_map_rotation,
        default_map_tilt: topicInfo.topic?.default_map_tilt,
        header_logo: undefined,
        header_url: undefined,
        header_color: undefined,
        header_text_color: undefined,
        cluster_accent_color: undefined,
        translation_languages: undefined,
        custom_map_style: undefined,
        custom_map_style_name: undefined,
        custom_social_media_image: undefined,
        marker_cluster_minimum: undefined,
        marker_cluster_threshold: undefined,
        featured_color: undefined,
        featured_name: undefined,
        custom_map_geojson: undefined,
        custom_map_geojson_style: undefined,
        custom_shapes: undefined,
        search_pill_settings: undefined,
        share_settings: undefined,
        custom_overlays: undefined,
        image_display: undefined,
        default_list_view: undefined,
        tag_info,
        pop_ups: undefined,
        comment_settings: undefined,
        add_point_customization: undefined,
        icon_size: undefined,
        show_easy_date_filters: undefined,
        creation_source: TopicCreationSource.INTERNAL_CREATE,
        point_email_capture_customization: undefined,
    };

    let createTopicResponse: CreateTopicResponse;
    await errorToast.tryOrErrorToast(async () => {
        createTopicResponse = await addTopic(
            amplitudeCallsite,
            createTopicRequest
        );

        if (pointCacheData.hasPoints && pointCacheData.hasFilterApplied && topicInfo.discoveryLevel !== DiscoveryLevel.Private) {
            const newTopic = await getTopic(
                amplitudeCallsite,
                createTopicResponse?._id.$oid,
                createTopicResponse.write_key,
                false
            );

            const newPointsWithCategoryName = pointCacheData.hasPoints
                ? pointCacheData.points
                    .map((p) => ({
                        _id: undefined,
                        email,
                        topic_id: createTopicResponse._id,
                        topic_category_name: topicInfo.categories.find(
                            (c) => c._id?.$oid === p.topic_category_id.$oid
                        )?.name,
                        location: p.location,
                        name: p.name,
                        description: p.description,
                        url: p.url,
                        special_offer: undefined,
                        active: true,
                        featured: false,
                        start: p.start,
                        end: p.end,
                        number: p.number,
                        is_gate_hidden: false,
                        custom_information: undefined,
                        embedded_video: p.embedded_video,
                        custom_icon_url: p.custom_icon_url,
                        custom_shape: p.custom_shape,
                        source: {
                            'source_type': 'ProxiMap',
                            'source_url': `${window.location.origin}/#discover-details?topic_id=${topicInfo.topicId}&point_id=${p._id.$oid}`,
                            'source_name': topicInfo.name
                        },
                        tag_info: p.tag_info
                }))
            : [];

            const newPoints: CreatePointRequest[] = newPointsWithCategoryName.map((p) => ({
                ...p,
                topic_category_id: (
                    newTopic.categories.find(
                        (newCategory) => newCategory.name === p.topic_category_name
                    )?._id
                ) as ObjectId,
                tag_info: p.tag_info ?? { user_tags: [], google_tags: [] }
            }));

            if (newPoints.length > 0) {
                await addPoints(
                    amplitudeCallsite,
                    createTopicResponse?._id.$oid,
                    newPoints,
                    createTopicResponse.write_key
                );
            }
        }

        // Open Link
        const newTopicLink = createTopicResponse.should_use_keyless_links
            ? createTopicResponse.cc_account_admin_link
            : createTopicResponse.cc_write_link;
        window.open(topicData.swapUrlForOrgUrl(newTopicLink), '_blank');
    }, 'An error has occurred.  Context: the server rejected the request due to validation issues.');
}
